import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Search,
  Sparkles,
  HeartPulse,
  Globe,
  ClipboardCheck,
  Lock,
  HandHeart,
  MessageCircleQuestion,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { BlobShape, LeafBranch, FloralAccent } from "../components/Organic";
import { FAQ_CATEGORIES } from "../lib/content";

const ICONS = {
  Sparkles, HeartPulse, Globe, ClipboardCheck, Lock, HandHeart,
};

export default function FAQ() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_CATEGORIES;
    return FAQ_CATEGORIES.map((cat) => {
      const items = cat.items.filter(
        (item) =>
          item.q.toLowerCase().includes(q) ||
          item.a.toLowerCase().includes(q)
      );
      return { ...cat, items };
    }).filter((cat) => cat.items.length > 0);
  }, [query]);

  const totalMatches = filtered.reduce((n, c) => n + c.items.length, 0);

  return (
    <div data-testid="page-faq">
      <Header query={query} onQueryChange={setQuery} totalMatches={totalMatches} />
      <CategoryNav categories={FAQ_CATEGORIES} />
      <Body categories={filtered} hasQuery={query.trim().length > 0} />
      <CtaSection />
    </div>
  );
}

function Header({ query, onQueryChange, totalMatches }) {
  return (
    <section className="relative pt-44 md:pt-52 pb-12 md:pb-14 px-6 md:px-10 overflow-hidden">
      <BlobShape
        className="absolute -top-20 -left-32 w-[520px] h-[520px]"
        color="#8DAA91"
        opacity={0.2}
      />
      <BlobShape
        className="absolute -bottom-32 -right-24 w-[420px] h-[420px]"
        color="#E4BE9E"
        opacity={0.22}
      />
      <FloralAccent
        className="absolute top-28 right-12 w-32 h-32 hidden md:block"
        color="#2A7B76"
        opacity={0.16}
      />

      <div className="relative max-w-5xl mx-auto text-center">
        <Reveal>
          <span className="overline">Frequently Asked Questions</span>
        </Reveal>
        <Reveal delay={1}>
          <h1 className="h-display mt-5 text-5xl md:text-6xl lg:text-7xl">
            Answers to help you{" "}
            <span className="h-italic-accent">feel prepared.</span>
          </h1>
        </Reveal>
        <Reveal delay={2}>
          <p className="mt-7 text-lg md:text-xl text-[var(--at-navy-soft)] max-w-3xl mx-auto leading-relaxed">
            If you're new to therapy — or just new to working with me — here
            are honest answers to the questions I hear most often. Don't see
            yours below? Reach out anytime.
          </p>
        </Reveal>

        <Reveal delay={3}>
          <div className="mt-9 max-w-xl mx-auto relative">
            <Search
              size={18}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-[var(--at-muted)]"
            />
            <input
              data-testid="faq-search"
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search questions… e.g. insurance, telehealth, ADHD"
              className="w-full pl-12 pr-5 py-4 rounded-full bg-[var(--at-bg)] border border-[var(--at-border)] focus:border-[var(--at-teal)] focus:outline-none text-[var(--at-navy)] font-sans"
            />
            {query.trim() && (
              <p
                data-testid="faq-search-count"
                className="mt-3 text-sm text-[var(--at-navy-soft)]"
              >
                {totalMatches === 0
                  ? "No matches — try a different word, or reach out below."
                  : `${totalMatches} match${totalMatches === 1 ? "" : "es"}`}
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CategoryNav({ categories }) {
  return (
    <section
      data-testid="faq-category-nav"
      className="px-6 md:px-10 pb-4"
    >
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="flex flex-wrap gap-2.5 justify-center">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                data-testid={`faq-nav-${cat.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--at-bg-2)] border border-[var(--at-border)] text-sm text-[var(--at-navy)] hover:bg-[var(--at-teal-tint)] hover:border-[var(--at-teal)] transition-colors"
              >
                {cat.title}
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Body({ categories, hasQuery }) {
  return (
    <section className="py-16 md:py-20 px-6 md:px-10 relative">
      <LeafBranch
        className="absolute top-12 left-6 w-32 h-32 hidden lg:block"
        color="#8DAA91"
        opacity={0.32}
      />
      <LeafBranch
        className="absolute bottom-12 right-6 w-40 h-40 hidden lg:block -scale-x-100"
        color="#2A7B76"
        opacity={0.28}
      />
      <div className="relative max-w-4xl mx-auto space-y-16 md:space-y-20">
        {categories.length === 0 && (
          <Reveal>
            <div
              data-testid="faq-empty"
              className="rounded-[2rem] border border-[var(--at-border)] bg-[var(--at-bg-2)] p-10 text-center"
            >
              <MessageCircleQuestion
                size={32}
                className="mx-auto text-[var(--at-teal)]"
              />
              <h3 className="mt-4 font-serif text-2xl text-[var(--at-navy)]">
                Nothing matched your search.
              </h3>
              <p className="mt-3 text-[var(--at-navy-soft)]">
                Send a quick note via the contact form — I'm happy to answer
                directly.
              </p>
              <Link
                to="/contact"
                data-testid="faq-empty-cta"
                className="btn-primary mt-6"
              >
                Ask Me Directly <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        )}

        {categories.map((cat) => {
          const Icon = ICONS[cat.icon] || Sparkles;
          return (
            <div
              key={cat.id}
              id={cat.id}
              data-testid={`faq-category-${cat.id}`}
              className="scroll-mt-44"
            >
              <Reveal>
                <div className="flex items-start gap-4 mb-6">
                  <div className="shrink-0 w-12 h-12 rounded-2xl bg-[var(--at-teal-tint)] flex items-center justify-center text-[var(--at-teal)]">
                    <Icon size={22} />
                  </div>
                  <div>
                    <h2 className="font-serif text-3xl md:text-4xl text-[var(--at-navy)] leading-snug">
                      {cat.title}
                    </h2>
                    <p className="mt-2 text-[var(--at-navy-soft)] leading-relaxed">
                      {cat.blurb}
                    </p>
                  </div>
                </div>
              </Reveal>

              <Accordion
                type="single"
                collapsible
                defaultValue={hasQuery ? `${cat.id}-0` : undefined}
                className="space-y-3"
              >
                {cat.items.map((item, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`${cat.id}-${idx}`}
                    data-testid={`faq-item-${cat.id}-${idx}`}
                    className="border-none rounded-[1.25rem] bg-[var(--at-bg)] border border-[var(--at-border)] px-5 md:px-6 overflow-hidden"
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${cat.id}-${idx}`}
                      className="py-5 hover:no-underline text-left font-serif text-lg md:text-xl text-[var(--at-navy)] leading-snug [&>svg]:text-[var(--at-teal)] [&>svg]:h-5 [&>svg]:w-5"
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`faq-content-${cat.id}-${idx}`}
                      className="pb-5 pr-2 text-[var(--at-navy-soft)] text-base leading-relaxed"
                    >
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="px-6 md:px-10 pb-24 pt-8" data-testid="faq-cta">
      <div className="max-w-5xl mx-auto rounded-[2.5rem] bg-[var(--at-navy)] text-[var(--at-bg)] p-10 md:p-16 text-center relative overflow-hidden">
        <BlobShape
          className="absolute -top-24 -right-20 w-[380px] h-[380px]"
          color="#2A7B76"
          opacity={0.3}
        />
        <BlobShape
          className="absolute -bottom-32 -left-20 w-[320px] h-[320px]"
          color="#8DAA91"
          opacity={0.22}
        />
        <div className="relative">
          <Reveal>
            <h2 className="h-display text-3xl md:text-5xl text-[var(--at-bg)]">
              Still have{" "}
              <span className="italic text-[var(--at-olive)]">questions?</span>
            </h2>
            <p className="mt-5 text-[var(--at-bg)]/80 max-w-xl mx-auto leading-relaxed">
              The free 15-minute consultation is the easiest way to ask
              anything else on your mind — no pressure, no commitment.
            </p>
            <Link
              to="/contact"
              data-testid="faq-bottom-cta"
              className="btn-primary mt-8 bg-[var(--at-olive)] text-[var(--at-navy)] hover:bg-[var(--at-bg)]"
            >
              Schedule a Free Consultation <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
